import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { parseTaxesDataForDisplayInTaxesForm } from '../../../helper/enum-parser';
import {
  EditTaxRatesMutation,
  GetTaxRateDataQuery,
} from '../../../mutations/TaxRatesMutations';
import { TaxesData } from '../../../types/location';
import Loader from '../../elements/Loader';
import FormSubmitFooter from '../FormSubmitFooter';
import { TaxesContainer } from './TaxesContainer';
import { z as zod } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

interface TaxesFormProps {
  partnerId: string;
}

const TaxesForm = ({ partnerId }: TaxesFormProps) => {
  const { data: taxData, isLoading: taxDataLoading } = GetTaxRateDataQuery({
    partnerId,
  });

  const taxRatesDataResponse = useMemo(
    () => parseTaxesDataForDisplayInTaxesForm(taxData as unknown as TaxesData),
    [taxData, parseTaxesDataForDisplayInTaxesForm],
  );

  const TaxDataSchema = zod
    .object({
      product_class_slug: zod.string().optional(),
      min_applicable: zod
        .string()
        .nullable()
        .optional()
        .refine((value) => !value || value?.length < 13, {
          message: 'Max number of digits is 12',
        })
        .refine((value) => !value || (value.length > 0 && Number(value) >= 0), {
          message: 'Tax price must be at least 0',
        })
        .refine((value) => !value || /^\d+(\.\d{1,2})?$/.test(value), {
          message: 'Max number of decimal spaces is 2',
        }),
      max_applicable: zod
        .string()
        .nullable()
        .optional()
        .refine((value) => !value || value?.length < 13, {
          message: 'Max number of digits is 12',
        })
        .refine((value) => !value || (value.length > 0 && Number(value) >= 0), {
          message: 'Tax price must be at least 0',
        })
        .refine((value) => !value || /^\d+(\.\d{1,2})?$/.test(value), {
          message: 'Max number of decimal spaces is 2',
        }),
      tax_rate_name: zod.string(),
      tax_rate: zod
        .string()
        .min(1, { message: 'Tax percentage is required' })
        .refine((value) => !value || Number(value) >= 0, {
          message: 'Tax must be between 0 and 25',
        })
        .refine((value) => !value || Number(value) <= 25, {
          message: 'Tax must be between 0 and 25',
        }),
      tax_id: zod.string(),
      id: zod.string(),
      product_class_name: zod.string(),
    })
    .superRefine(({ min_applicable, max_applicable }, ctx) => {
      if (min_applicable === '')
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: 'Tax price is required',
          path: ['min_applicable'],
        });

      if (max_applicable === '')
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: 'Tax price is required',
          path: ['max_applicable'],
        });

      const numberMin = Number(min_applicable);
      const numberMax = Number(max_applicable);

      // If there is no maxPrice defined, minPrice can not be 0
      if (
        (numberMax && numberMin < 0) ||
        (!numberMax && numberMin <= 0 && max_applicable !== '')
      )
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: 'Must be greater than 0',
          path: ['min_applicable'],
        });

      // If maxPrice is defined, minPrice can not be greater than maxPrice
      if (numberMax && numberMin >= numberMax)
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: 'Can not be greater than max price',
          path: ['min_applicable'],
        });

      // maxPrice can not be 0 never
      if (numberMax === 0)
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: 'Must be greater than 0',
          path: ['max_applicable'],
        });

      // If minPrice is defined, maxPrice must be greater than minPrice
      if (numberMax && numberMin && numberMin >= 0 && numberMax <= numberMin)
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: 'Must be greater than min price',
          path: ['max_applicable'],
        });
    });

  const Schema = zod.object({
    data: zod.array(TaxDataSchema),
  });

  type FormSchemaType = zod.infer<typeof Schema>;

  const methods = useForm<FormSchemaType>({
    defaultValues: {
      data: taxRatesDataResponse,
    },
    resolver: zodResolver(Schema),
    shouldUnregister: false,
    mode: 'all',
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty, isLoading, errors },
  } = methods;

  const numberOfErrors = errors.data?.length ?? 0;

  const editTaxRateDataMutation = EditTaxRatesMutation({ partnerId });

  const onSubmit = (data: TaxesData) => {
    editTaxRateDataMutation.mutate(data?.data);
  };

  useEffect(() => {
    if (taxRatesDataResponse) {
      reset({
        data: taxRatesDataResponse,
      });
    }
  }, [taxRatesDataResponse, reset]);

  if (isLoading || taxDataLoading) {
    return (
      <div className='pt-5 pb-6'>
        <div className='flex justify-center items-center w-full h-screen'>
          <Loader fillColor='#6923F4' size='48' />
        </div>
      </div>
    );
  }

  return (
    <div className='pb-6'>
      <FormProvider {...methods}>
        <TaxesContainer
          partnerId={partnerId}
          taxRatesDataResponse={taxRatesDataResponse}
        />
      </FormProvider>

      {taxRatesDataResponse && (
        <FormSubmitFooter
          handleSubmit={handleSubmit(onSubmit)}
          loading={editTaxRateDataMutation.isPending}
          disabled={!isDirty || numberOfErrors > 0}
        />
      )}
    </div>
  );
};

export default TaxesForm;
